import { NEW_SORT, SORT } from "../../constants";
import { database } from "../index";
import { CUSTOMER_SCHEMA, BUSINESS_SCHEMA } from "../schema";
import { Q } from "@nozbe/watermelondb";

const customer = database.collections.get(CUSTOMER_SCHEMA);

export const getCustomerByID = (id) =>
  customer
    .query(Q.where("id", id))
    .observeWithColumns([
      "name",
      "phone",
      "transaction_sms",
      "sms",
      "total_outstanding_payment",
      "transaction_sms",
      "collect_at",
    ]);

export const findCustomerByID = async (id) => await customer.find(id);

export const fetchCustomerByID = async (id) =>
  await customer.query(Q.where("id", id)).fetch();

export const getCollectionCount = async () =>
  await customer.query(Q.where("collect_at", Q.notEq(null))).fetchCount();

export const getAllCustomers = async () => await customer.query().fetch();

export const observeActiveCustomers = (user) =>
  customer
    .query(
      Q.on(BUSINESS_SCHEMA, [
        Q.where("user_id", user.id),
        Q.where("is_active", true),
      ]),
    )
    .observeWithColumns([
      "name",
      "phone",
      "transaction_sms",
      "sms",
      "total_outstanding_payment",
      "transaction_sms",
      "collect_at",
    ]);

export const observeActiveCustomerCount = () =>
  customer.query(Q.on(BUSINESS_SCHEMA, "is_active", true)).observeCount();

export const getCustomersByBusiness = async (id) =>
  await customer.query(Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const getCustomersWithphoneNumberByBusiness = async (id) =>
  await customer
    .query(Q.on(BUSINESS_SCHEMA, "id", id), Q.where("phone", Q.notEq("")))
    .fetch();

export const observeCustomersByBusiness = async (id) =>
  await customer.query(Q.on(BUSINESS_SCHEMA, "id", id)).observe();

export const getCustomersByBusinessAndType = async (id, is_supplier) =>
  await customer
    .query(Q.on(BUSINESS_SCHEMA, "id", id), Q.where("is_supplier", is_supplier))
    .fetch();


export const getFilteredCustomers = async (id, filters, limit, offset) => {
  let {
    amount, // { min, max }
    customers, // "customer" | "supplier"
    sort,
    keywords, // searchText
    type, // array: ["Settled", "Payables", "Receivables"]
    date,
  } = filters;

  const start = date?.startDate;
  const end = date?.endDate;

  let filterQuery = [];

  // TYPE FILTER (MULTI)
  let typeCondition = [];
  if (type?.length > 0) {
    let conditions = [];

    if (type.includes("Settled")) {
      conditions.push(Q.where("total_outstanding_payment", Q.eq(0)));
    }

    if (type.includes("Payables")) {
      conditions.push(Q.where("total_outstanding_payment", Q.lt(0)));
    }

    if (type.includes("Receivables")) {
      conditions.push(Q.where("total_outstanding_payment", Q.gt(0)));
    }

    if (conditions.length > 0) {
      typeCondition.push(Q.or(...conditions));
    }
  }

  // DATE FILTER
  if (start) {
    filterQuery.push(Q.where("last_transaction_at", Q.gte(start.getTime())));
  }

  if (end) {
    filterQuery.push(Q.where("last_transaction_at", Q.lte(end.getTime())));
  }

  if (amount) {
  const min = Number(amount.min) || 0;
  const max = amount.max !== undefined && amount.max !== "" ? Number(amount.max) : null;

  if (min > 0 || max !== null) {
    if (max !== null) {
      // Both min and max → ± max range
      filterQuery.push(
        Q.or(
          Q.and(
            Q.where("total_outstanding_payment", Q.gte(min)),
            Q.where("total_outstanding_payment", Q.lte(max))
          ),
          Q.and(
            Q.where("total_outstanding_payment", Q.gte(-max)),
            Q.where("total_outstanding_payment", Q.lte(-min))
          )
        )
      );
    } else {
      // Only min → ± infinite range
      filterQuery.push(
        Q.or(
          Q.where("total_outstanding_payment", Q.gte(min)),
          Q.where("total_outstanding_payment", Q.lte(-min))
        )
      );
    }
  }
}

 

  // CUSTOMER / SUPPLIER
  let isSupplierCondition = [];
  if (customers?.toLowerCase() === "suppliers") {
    isSupplierCondition.push(Q.where("is_supplier", true));
  } else if (customers?.toLowerCase() === "customers") {
    isSupplierCondition.push(Q.where("is_supplier", false));
  }

  // SEARCH
  let searchCondition = [];
  if (keywords?.trim()) {
    const search = Q.sanitizeLikeString(keywords.trim());

    searchCondition.push(
      Q.or(
        Q.where("name", Q.like(`%${search}%`)),
        Q.where("phone", Q.like(`%${search}%`)),
      ),
    );
  }

  // BASE QUERY (🔥 FIX: typeCondition added)
  let query = customer.query(
    Q.on(BUSINESS_SCHEMA, "id", id),
    ...isSupplierCondition,
    ...searchCondition,
    ...typeCondition, // ✅ THIS WAS MISSING
    ...filterQuery,
  );

  // SORTING
  switch (sort) {
    case NEW_SORT.amountHighToLow:
      query = query.extend(Q.sortBy("total_outstanding_payment", Q.desc));
      break;

    case NEW_SORT.amountLowToHigh:
      query = query.extend(Q.sortBy("total_outstanding_payment", Q.asc));
      break;

    case NEW_SORT.nameAToZ:
      query = query.extend(Q.sortBy("name", Q.asc));
      break;

    case NEW_SORT.nameZToA:
      query = query.extend(Q.sortBy("name", Q.desc));
      break;

    case NEW_SORT.Oldest:
      query = query.extend(Q.sortBy("last_transaction_at", Q.asc));
      break;

    case NEW_SORT.Newest:
    default:
      query = query.extend(Q.sortBy("last_transaction_at", Q.desc));
      break;
  }

  // PAGINATION LAST
  if (limit !== undefined) {
    query = query.extend(Q.take(limit));
  }

  if (offset !== undefined) {
    query = query.extend(Q.skip(offset));
  }

  return await query.fetch();
};

export const getFilteredPendingAmount = async (id, filters) => {
  let filterQuery = [];
  filterQuery.push(Q.where("total_outstanding_payment", Q.gt(0)));

  let data = await customer
    .query(
      Q.on(BUSINESS_SCHEMA, "id", id),
      Q.where("last_transaction_at", Q.lte(filters.end)),
      Q.where("last_transaction_at", Q.gte(filters.start)),
      Q.or(
        Q.where(
          "name",
          Q.like(`%${Q.sanitizeLikeString(filters.searchText ?? "")}%`),
        ),
        Q.where(
          "phone",
          Q.like(`%${Q.sanitizeLikeString(filters.searchText ?? "")}%`),
        ),
      ),
      ...filterQuery,
    )
    .fetch();

  return data;
};

export const getCustomerOfBusiness = (id) =>
  customer
    .query(Q.where("business_id", id))
    .observeWithColumns([
      "name",
      "phone",
      "transaction_sms",
      "sms",
      "total_outstanding_payment",
      "transaction_sms",
      "collect_at",
    ]);

export const getCustomerByBusinessID = (id) =>
  customer
    .query(Q.where("business_id", id), Q.where("is_supplier", false))
    .observeWithColumns([
      "name",
      "phone",
      "transaction_sms",
      "sms",
      "total_outstanding_payment",
      "transaction_sms",
      "collect_at",
    ]);

export const getSupplierByBusinessID = (id) =>
  customer
    .query(Q.where("business_id", id), Q.where("is_supplier", true))
    .observeWithColumns([
      "name",
      "phone",
      "transaction_sms",
      "sms",
      "total_outstanding_payment",
      "transaction_sms",
      "collect_at",
    ]);

export const deleteCustomerByID = async (id) => {
  const getCustomer = await customer.find(id);
  await getCustomer.markAsDeleted();
};

export const bulkUpdateCustomers = async (customers, date) => {
  await database.write(async () => {
    const allRecords = await prepareUpdation(customers, date);
    await database.batch(...allRecords);
  });
};

const prepareUpdation = async (customers, date) => {
  return await Promise.all(
    customers.map(async (getCustomer) => {
      try {
        const c = await customer.find(getCustomer.id);
        return c.prepareUpdate((con) => {
          con.collect_at = date;
        });
      } catch (err) {
      }
    }),
  );
};

export const calculateTotalOutstanding = async (customer) => {
  let payments = await customer.payment.fetch();

  let total = payments.reduce((value, data) => {
    return data.payment_mode === "Udhaar"
      ? (value += data.amount)
      : (value -= data.amount);
  }, 0);

  return total;
};

export const bulkDeleteCustomersByBusiness = async (business_id) => {
  await database.write(async () => {
    let customers = await getCustomersByBusiness(business_id);
    const allRecords = await prepareDeletion(customers);
    await database.batch(...allRecords);
  });
};

const prepareDeletion = async (customers) => {
  return await Promise.all(
    customers.map(async (customer) => {
      try {
        return customer.prepareMarkAsDeleted();
      } catch (err) {
      }
    }),
  );
};
